import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { IPopupProps } from './types.type';
import {
  FormContainer,
  PopupContainer,
  SixDigitInput
} from '../../common/components';
import { CodeErrors } from '../../auth/forms/validation.pattern';
import { settingsService } from '../service/settings.service';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { authService } from '../../auth/service/auth.service';
import { useThrottle } from '../../hooks/useThrottle';
import { useCodeRegister } from '../../hooks/codeRegister';

type Inputs = {
  code: string;
};

export const ConfirmEmail = ({ isOpen, onClose, email }: IPopupProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors }
  } = useForm<Inputs>();

  const resend = useMutation({
    mutationKey: ['resendEmailConfirmation'],
    mutationFn: async (resendEmail: string) => {
      return settingsService.resendEmailConfirmation({ email: resendEmail });
    },
    onSuccess: () => {
      toast.success(t('settings.codeResent'));
    },
    onError: () => {
      toast.error(t('auth.resendFailed'));
    }
  });
  const throttledResend = useThrottle(resend.mutate);

  const positionStyles = errors.code ? 'bottom-14' : 'bottom-5';

  const activate = useMutation({
    mutationKey: ['confirmEmail'],
    mutationFn: async (data: Inputs) => {
      return settingsService.confirmEmail({ code: data.code });
    },
    onSuccess: async () => {
      onClose();
      toast.success(t('settings.emailChangedSignOut'));
      await authService.logout();
      setTimeout(() => {
        window.location.href = ROUTER_LINKS.SIGNIN;
      }, 1000);
    },
    onError: (err: any) => {
      if (err.response.data?.message === 'Invalid code') {
        setError('code', {
          message: CodeErrors.incorrect,
          type: 'manual'
        });
      }
      if (err.response.data?.message === 'Email already confirmed') {
        setError('code', {
          message: CodeErrors.alreadyCofirmed,
          type: 'manual'
        });
      }
      if (err.response.data?.message === 'Code expired') {
        setError('code', {
          message: CodeErrors.expired,
          type: 'manual'
        });
      }
    }
  });
  const throttledActivate = useThrottle(activate.mutate);

  const {
    codeRegister,
    resendCodeHandler,
    onSubmit,
    formattedValue,
    setFormattedValue
  } = useCodeRegister({
    register,
    throttledResendCode: throttledResend,
    throttledSubmit: throttledActivate,
    handleSubmit,
    setValue,
    email
  });

  return (
    <PopupContainer
      caption={t('settings.confirmNewEmail')}
      isOpen={isOpen}
      onClose={onClose}
      description={
        <div className="text-left mb-[26px] md:mb-[36px]">
          <span className="text-gray-900 font-body text-[16px] font-regular">
            {t('settings.enterConfirmationCode')} {email ?? ''}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-base-black ml-[10px] font-medium hover:text-blue-500 cursor-pointer bg-transparent border-0 p-0"
          >
            {t('settings.changeLink')}
          </button>
        </div>
      }
    >
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <SixDigitInput
          errors={errors}
          name="code"
          setFormattedValue={setFormattedValue}
          formattedValue={formattedValue}
          register={codeRegister}
        >
          <></>
        </SixDigitInput>
        <div className="flex gap-[10px] justify-center text-center my-[24px]">
          <p className="text-[16px] font-regular font-body text-gray-900">
            {t('auth.dontSeeCode')}
          </p>
          <div
            className={`flex right-4 ${positionStyles} cursor-pointer`}
            onClick={resendCodeHandler}
          >
            <span className="text-[16px] font-medium font-body text-base-black hover:text-blue-600">
              {t('auth.resendCode')}
            </span>
          </div>
        </div>
      </FormContainer>
    </PopupContainer>
  );
};
